import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router';
import { Toaster } from '@butter/ui/patterns';
import { ErrorBoundary } from '~/lib/error/ErrorBoundary';
import { GlobalErrorHandler } from '~/lib/error/GlobalErrorHandler';
import { SHOW_DEV_TOOLS } from '~/config/env';
import { AuthWatcher } from '~/services/auth/components/AuthWatcher';
import { ClubDataPrefetcher } from '~/services/internalApi/club';
import { TenantProvider } from '~/providers';
import { MissingParamsGuard } from '~/lib/guards/MissingParamsGuard';

// Lazy so the panel (and the tools it registers) stays out of the main chunk —
// it's only ever rendered behind SHOW_DEV_TOOLS.
const DevTools = lazy(() =>
  import('~/features/dev-tools/DevTools').then((m) => ({ default: m.DevTools })),
);

/**
 * Root layout — providers, error boundaries, dev tools.
 *
 * `MissingParamsGuard` is deliberately NOT here. It used to wrap this `<Outlet />`, which
 * made "has club-id and tenant-handle in the URL" a precondition of every route in the app.
 * The SMS unlock page has neither by design — the token is the only thing in the URL, and
 * the exchange supplies the rest — so the guard moved down into `GuardedLayout` below, and
 * `/unlock` mounts as a sibling of it.
 *
 * Everything at this level still applies to both: tenant branding, error boundaries, toasts.
 */
export default function App() {
  return (
    <GlobalErrorHandler>
      {/* Outermost so every surface — including the ErrorBoundary fallback, the
          MissingParamsGuard fallback and the ungated auth routes — renders against
          the tenant's brand, and so TenantLogo/TenantBrand can read the handle
          from context. */}
      <TenantProvider>
        <ErrorBoundary>
          <ClubDataPrefetcher />
          <div className="min-h-screen bg-background">
            <Outlet />
          </div>

          {/* Dev Tools (local dev + staging only) */}
          {SHOW_DEV_TOOLS && (
            <Suspense fallback={null}>
              <DevTools />
            </Suspense>
          )}
        </ErrorBoundary>

        {/* Outside ErrorBoundary, inside TenantProvider — both deliberate.
            ErrorBoundary renders its fallback *instead of* children, so a Toaster
            nested inside it unmounts on a crash and every later toast renders
            nowhere — including GlobalErrorHandler's, whose whole job is surfacing
            the async failures that escape the boundary. It stays under
            TenantProvider because CountdownToastView (the registerVersionToast
            countdown) paints with `--theme-*`. */}
        <Toaster position="top-right" />
      </TenantProvider>
    </GlobalErrorHandler>
  );
}

/**
 * Layout for every route that needs a club and tenant in the URL — which is all of them
 * except the unlock link.
 *
 * `AuthWatcher` lives here rather than in `App` for the same reason: the unlock page has no
 * session and never will, so watching for one is noise at best.
 */
export function GuardedLayout() {
  return (
    <MissingParamsGuard>
      <AuthWatcher />
      <Outlet />
    </MissingParamsGuard>
  );
}
